import type { Component } from 'vue'
import { defineAsyncComponent } from 'vue'

// Elements and blocks are both registered as async components.
// SSR resolves them inside <NuxtPage>'s Suspense boundary, so the
// page's HTML contains the rendered output. The client modulepreload
// set only contains chunks the page actually rendered — PDP-only
// elements no longer leak into category-page preloads.
// Local layers (~/layers/*) can contribute their own CMS elements.
const elementLoaders = import.meta.glob<Component>(
    [
        '../components/structure/element/StructureElement*.vue',
        '../../layers/*/app/components/structure/element/StructureElement*.vue',
    ],
    {
        eager: false,
        import: 'default'
    }
)

const blockLoaders = import.meta.glob<Component>(
    '../components/structure/block/StructureBlock*.vue',
    {
        eager: false,
        import: 'default'
    }
)

const elementRegistry = new Map<string, Component>()
Object.entries(elementLoaders).forEach(([path, loader]) => {
    const match = path.match(/StructureElement([A-Za-z0-9]+)\.vue$/)
    if (match) {
        const componentName = `StructureElement${match[1]}`
        elementRegistry.set(componentName, defineAsyncComponent(loader as () => Promise<Component>))
    }
})

const blockRegistry = new Map<string, () => Promise<Component>>()
Object.entries(blockLoaders).forEach(([path, loader]) => {
    const match = path.match(/StructureBlock([A-Za-z0-9]+)\.vue$/)
    if (match) {
        const componentName = `StructureBlock${match[1]}`
        blockRegistry.set(componentName, loader as () => Promise<Component>)
    }
})

export default function useCmsComponentRegistry() {
    /**
     * Get component - eager first, then lazy fallback
     * @param name - Component name (e.g., "StructureBlockImageText")
     * @param options - Loading options
     */
    function getCmsComponent(
        name: string,
        options: { lazy?: boolean } = {}
    ): Component | undefined {
        const elementComponent = elementRegistry.get(name)
        if (elementComponent) {
            return elementComponent
        }

        if (options.lazy) {
            const loader = blockRegistry.get(name)
            if (loader) {
                return defineAsyncComponent({
                    loader: loader,
                    delay: 200,
                    timeout: 10000,
                    errorComponent: undefined,
                    loadingComponent: undefined,
                })
            }
        } else {
            console.warn(`[CMS Registry] Block ${name} not in element registry`)
        }

        return undefined
    }

    function getElementComponent(name: string): Component | undefined {
        return elementRegistry.get(name)
    }

    function getBlockComponent(name: string): Component | undefined {
        return getCmsComponent(name, { lazy: true })
    }

    return {
        getCmsComponent,
        getElementComponent,
        getBlockComponent,
    }
}
